import React, { Component } from 'react'
import { isAuthenticated, getProfile } from 'tools/user'
import PropTypes from 'prop-types'
import RootErrorBoundary from 'atoms/RootErrorBoundary'
import { settings } from 'contexts/settings-context'
import { query } from 'tools/fetch'

const { Provider, Consumer } = React.createContext({})

const BALANCE_QUERY = `
query WalletBalance {
    currentUser {
        wallet {
            playableBalance
            bonuses {
                offerPublicId
                balance
                applicableClients
                redirectUri
                destinationUri
                claimedAt
                bonusAmount
            }
        }
    }
}`

const BALANCE_CACHE_KEY = 'shgn::balance'
const BALANCE_EXPIRES_CACHE_KEY = 'shgn::balance-expires'
const SECOND = 1000
const BALANCE_CACHE_VALID_FOR = 30 * SECOND
const BALANCE_CACHE_JITTER = 10 * SECOND

class ProfileProvider extends Component {
  state = {
    profile: null,
    playableBalance: null,
    isAuthenticated: null,
  }

  get walletApiUrl () {
    const { config } = this.props
    const apiUrl = config.walletApiUrl || settings.WALLET_API_URI
    return apiUrl
  }

  componentDidMount () {
    const { config } = this.props
    if (config.suppressAuthUI === true) {
      return // nothing to do here if we don't intend to render any of this.
    }
    const userIsAuthenticated = isAuthenticated(config.idToken)
    const profile = getProfile(config.idToken)
    this.setState({
      profile: profile,
      isAuthenticated: userIsAuthenticated,
    })

    if (!config.accessToken || userIsAuthenticated === false) {
      return
    }

    // SPA apps might want to force
    if (config.disableBalanceCache) {
      this.forceUpdateBalance()
    } else {
      this.updateBalance()
    }

    this.addIdentityToDataLayer(profile)

    // subscribe to change in localstorage
    window.addEventListener('storage', this.onStorageChanged)
  }

  onStorageChanged = (e) => {
    // if another window updates the stored balance
    if (e.key === BALANCE_CACHE_KEY) {
      const { playableBalance, bonuses } = JSON.parse(window.localStorage[BALANCE_CACHE_KEY])
      this.setState({ playableBalance, bonuses })
    } else if (e.key === BALANCE_EXPIRES_CACHE_KEY && e.newValue === null) {
      this.forceUpdateBalance()
    }
  }

  componentWillUnmount () {
    window.removeEventListener('storage', this.onStorageChanged)
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle)
    }
  }

  render () {
    const { profile, playableBalance, bonuses, isAuthenticated } = this.state
    const { children } = this.props

    return (
      <RootErrorBoundary>
        <Provider value={{ playableBalance, bonuses, isAuthenticated, ...profile }}>
          {children}
        </Provider>
      </RootErrorBoundary>
    )
  }

  async updateBalance (canScheduleUpdate = true) {
    // if we have no cache or the cache is already old, force an update
    const now = Date.now()
    if (
      !window.localStorage ||
      !window.localStorage[BALANCE_CACHE_KEY] ||
      !window.localStorage[BALANCE_EXPIRES_CACHE_KEY] ||
      Number(window.localStorage[BALANCE_EXPIRES_CACHE_KEY]) < now
    ) {
      return this.forceUpdateBalance()
    }

    // use existing cache
    const { playableBalance, bonuses } = JSON.parse(window.localStorage[BALANCE_CACHE_KEY])
    this.setState({ playableBalance, bonuses })

    // schedule an update after the cache expires + a few seconds of jitter
    // to prevent multiple tabs from doing this update simultaneously
    if (canScheduleUpdate) {
      const timeout = Number(window.localStorage[BALANCE_EXPIRES_CACHE_KEY]) -
        now +
        Math.floor(Math.random() * BALANCE_CACHE_JITTER)
      this.timeoutHandle = setTimeout(() => this.updateBalance(false), timeout)
    }
  }

  async forceUpdateBalance () {
    const { config } = this.props

    const queryDetails = {
      uri: this.walletApiUrl,
      accessToken: config.accessToken,
      query: BALANCE_QUERY,
    }

    const data = await query(queryDetails)
    if (data) {
      const { playableBalance, bonuses } = data.currentUser.wallet
      this.setState({ playableBalance, bonuses })

      // store balance
      if (window.localStorage) {
        window.localStorage[BALANCE_CACHE_KEY] = JSON.stringify({
          playableBalance,
          bonuses,
        })
        window.localStorage[BALANCE_EXPIRES_CACHE_KEY] = Date.now() + BALANCE_CACHE_VALID_FOR
      }
    }
  }

  addIdentityToDataLayer (profile) {
    if (window.dataLayer !== undefined && profile) {
      window.dataLayer.push({
        user: {
          uuid: profile.id,
          email: profile.email,
          username: profile.preferred_username,
          givenName: profile.given_name,
          familyName: profile.family_name,
        },
        event: 'identify',
      })
    }
  }
}

const ProfileConsumer = Consumer

export {
  ProfileProvider,
  ProfileConsumer,
}

ProfileProvider.propTypes = {
  config: PropTypes.object.isRequired,
  children: PropTypes.object.isRequired,
}



// WEBPACK FOOTER //
// ./src/contexts/profile-context.js